// Orquestra a gravação dos campos do formulário em DadosRequerimento: primeiro
// descobre ao vivo os modeloRequerimentoCampoId reais (GET /modelo-requerimento/:id/campos
// — esses IDs são específicos de cada modeloRequerimento e não podem ser fixados no
// config.js), depois envia os valores preenchidos (POST /requerimento/:id/dados).
//
// `dadosRequerimentoConfig` do pedido tem 3 seções, todas opcionais:
//  - campos: [{ field, campoNome, convert?, primeiro? }] — valor lido direto do
//    formData. `convert(valor)` é opcional, pra campos lookup cujo catálogo já é
//    conhecido (Sexo, Etnia, Estado Civil, Doador de Órgãos, Servidor Público —
//    ver config/shared/catalogosPessoaFisica.js); sem `convert`, o valor é
//    enviado como texto puro. `primeiro: true` extrai o 1º item de um campo que
//    é array no formData (ex: CLI_Comissao a partir do multiSelect de comissões).
//    Cada entrada usa `campoNome` (resolvido ao vivo contra o GET, o padrão) OU
//    `modeloRequerimentoCampoId` fixo (bypassa o GET/lookup por nome) — só faz
//    sentido quando o dono do catálogo já informou o ID literal daquele campo
//    pra esse modeloRequerimento específico (não é um valor "inventado": é a
//    mesma autoridade que informa tipoDocumentoProcesso/tipoLancamentoId).
//  - enderecos: [{ cepField, campoNomeCep, campoNomeEstado, campoNomeMunicipio, campoNomePais? }] —
//    resolvidos via 1 chamada à API de Endereços (CEP → cepId/estadoId/municipioId/paisId),
//    confirmado com o usuário que são os mesmos IDs de referência que a API de
//    Protocolo espera nesses 3 campos lookup de endereço.
//  - municipiosPorNome: [{ field, campoNome }] — campos de texto livre cujo valor é
//    um nome de município (Naturalidade, Município do Título de Eleitor) e cujo
//    campoNome espera o ID desse catálogo; resolvidos por busca exata via
//    addressService.searchCities (mesma lógica de perfilPrefillService.js). Só
//    resolve quando exatamente 1 resultado bate com o nome completo — ambíguo ou
//    não encontrado não arrisca, o campo simplesmente não é enviado.
//
// Campos lookup cujo catálogo ainda não temos (Órgão Público, Nacionalidade,
// Universidade) continuam de fora. Ver [[requerimentos-protocolo-melhorias]].
import { protocoloService } from '../../../../services';
import { resolverEnderecoPorCep, resolverMunicipioPorNome } from './enderecoResolverService';
import { criteriosCepDoFormulario } from '../../../address/utils/cepRegistros';
import { removerCamposOcultos } from '../utils/validation';
import logger from '../../../../utils/logger';

const resolverCamposTexto = (campos = [], formData, campoIdPorNome, modeloRequerimentoId) => {
  return campos
    .map(({ field, campoNome, modeloRequerimentoCampoId: campoIdFixo, convert, primeiro }) => {
      let valor = formData?.[field];
      if (primeiro && Array.isArray(valor)) {
        valor = valor[0];
      }
      if (convert && valor !== undefined && valor !== null && valor !== '') {
        valor = convert(valor);
      }

      const modeloRequerimentoCampoId = campoIdFixo ?? campoIdPorNome.get(campoNome);

      if (!modeloRequerimentoCampoId) {
        logger.warn(`dadosRequerimentoConfig referencia campoNome "${campoNome}" que não existe no modeloRequerimento ${modeloRequerimentoId}`);
        return null;
      }
      if (valor === undefined || valor === null || valor === '' || Number.isNaN(valor)) {
        return null;
      }

      return { modeloRequerimentoCampoId, valor: String(valor) };
    })
    .filter(Boolean);
};

// Os fragmentos de endereço (DADOS_REQUERIMENTO_RESIDENCIAL/PROFISSIONAL em
// enderecoSections.js/sociedadeSections.js) são compartilhados por vários
// pedidos, mas nem todo modeloRequerimento tem os mesmos sub-campos de endereço
// configurados (ex: alguns não têm Complemento/Telefone/País) — por isso, ao
// contrário de resolverCamposTexto, aqui um campoNome não encontrado é
// silenciosamente ignorado, sem warning: é a variação esperada entre modelos,
// não um erro de configuração.
const resolverCamposEndereco = async (enderecos = [], formData, campoIdPorNome) => {
  const resultados = await Promise.all(enderecos.map(async ({ cepField, campoNomeCep, campoNomeEstado, campoNomeMunicipio, campoNomePais }) => {
    const resolvido = await resolverEnderecoPorCep(formData?.[cepField], criteriosCepDoFormulario(formData, cepField));
    if (!resolvido) return [];

    return [
      { campoNome: campoNomeCep, valor: resolvido.cepId },
      { campoNome: campoNomeEstado, valor: resolvido.estadoId },
      { campoNome: campoNomeMunicipio, valor: resolvido.municipioId },
      // Opcional: só os modelos que têm o campo de País declaram campoNomePais
      ...(campoNomePais ? [{ campoNome: campoNomePais, valor: resolvido.paisId }] : [])
    ];
  }));

  return resultados
    .flat()
    .map(({ campoNome, valor }) => {
      const modeloRequerimentoCampoId = campoIdPorNome.get(campoNome);
      if (!modeloRequerimentoCampoId || valor === undefined || valor === null) return null;

      return { modeloRequerimentoCampoId, valor: String(valor) };
    })
    .filter(Boolean);
};

// Assim como os campos de endereço, um município que não resolver (ambíguo, não
// encontrado, ou campoNome inexistente nesse modeloRequerimento) é ignorado em
// silêncio — não é um erro de configuração, é a variação esperada entre modelos
// e a limitação conhecida de resolver por nome (não por autocomplete).
const resolverMunicipiosPorNome = async (municipiosPorNome = [], formData, campoIdPorNome) => {
  const resultados = await Promise.all(municipiosPorNome.map(async ({ field, campoNome }) => {
    const municipioId = await resolverMunicipioPorNome(formData?.[field]);
    if (!municipioId) return null;

    const modeloRequerimentoCampoId = campoIdPorNome.get(campoNome);
    if (!modeloRequerimentoCampoId) return null;

    return { modeloRequerimentoCampoId, valor: String(municipioId) };
  }));

  return resultados.filter(Boolean);
};

/**
 * @param {Object} params
 * @param {string} params.requerimentoId
 * @param {string|number} params.usuarioId
 * @param {string} params.modeloRequerimentoId - modeloRequerimento já resolvido (não o condicional cru)
 * @param {Object} params.formData
 * @param {{campos?: Array, enderecos?: Array, municipiosPorNome?: Array}} params.dadosRequerimentoConfig - mapeamento local declarado no config.js do pedido
 * @param {Array} [params.steps] - etapas do pedido; campos escondidos por `showWhen` não são enviados
 * @returns {Promise<Object|null>} resposta da API, ou null se não havia nada pra enviar
 */
export async function salvarDadosRequerimento({ requerimentoId, usuarioId, modeloRequerimentoId, formData: formDataCompleto, dadosRequerimentoConfig, steps }) {
  if (!dadosRequerimentoConfig) {
    return null;
  }

  // Sem o CEP escondido, os IDs de endereço daquele bloco também deixam de ir
  const formData = removerCamposOcultos(formDataCompleto, steps);

  const response = await protocoloService.listarCamposModeloRequerimento(modeloRequerimentoId);
  const camposApi = response.data?.campos || [];
  const campoIdPorNome = new Map(camposApi.map((c) => [c.campoNome, c.modeloRequerimentoCampoId]));

  const [camposTexto, camposEndereco, camposMunicipio] = await Promise.all([
    resolverCamposTexto(dadosRequerimentoConfig.campos, formData, campoIdPorNome, modeloRequerimentoId),
    resolverCamposEndereco(dadosRequerimentoConfig.enderecos, formData, campoIdPorNome, modeloRequerimentoId),
    resolverMunicipiosPorNome(dadosRequerimentoConfig.municipiosPorNome, formData, campoIdPorNome)
  ]);

  const campos = [...camposTexto, ...camposEndereco, ...camposMunicipio];

  if (campos.length === 0) {
    return null;
  }

  return protocoloService.salvarDadosRequerimento(requerimentoId, { usuarioId: Number(usuarioId), campos });
}
